import React, { type PropsWithChildren } from 'react';
import { KeyboardAvoidingView, Platform, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { Main } from '@expo/html-elements';
import { theme } from '@garnish/constants';
import { useResponsive } from '@garnish/core';
import { ScrollViewWithHeaderTitle, useNavbarContainerHeight } from '@garnish/navigation';

import { AppFooter } from '../../../../components';
import { useIsFoldableDevice } from '../../../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardCheckoutContainer = (props: PropsWithChildren) => {
  const { children } = props;

  const { currentBreakpoint, minWidth, match } = useResponsive();
  const { height: navbarHeight } = useNavbarContainerHeight();
  const { top: safeAreaTop } = useSafeAreaInsets();
  const isFoldableDevice = useIsFoldableDevice();
  const navbarPaddingBottomStyle = useStyle(
    () => ({ paddingBottom: navbarHeight + theme.spacing['6'] }),
    [navbarHeight],
  );

  // ─── Derived ───────────────────────────────────────────────────────────────

  const shouldApplyNavbarPadding = currentBreakpoint.isXS;
  const shouldApplyTopSafeArea = currentBreakpoint.isXS && isFoldableDevice;

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerDynamicStyles = match([
    styles.containerXS,
    styles.containerSM,
    styles.containerMD,
  ]);
  const containerStyles = [styles.container, containerDynamicStyles];
  const topSafeAreaStyles = useStyle(
    () => ({ paddingTop: Math.max(safeAreaTop + theme.spacing['4'], theme.spacing['6']) }),
    [safeAreaTop],
  );
  const scrollContainerNavbarStyles = useStyle(
    () => ({ paddingTop: navbarHeight + theme.spacing['12'] }),
    [navbarHeight],
  );
  const scrollContainerStyles = [
    match([styles.scrollContainerXS, styles.scrollContainerSM]),
    shouldApplyNavbarPadding ? navbarPaddingBottomStyle : undefined,
    shouldApplyTopSafeArea ? topSafeAreaStyles : undefined,
    minWidth.isSM ? scrollContainerNavbarStyles : undefined,
  ];

  // ─────────────────────────────────────────────────────────────────────

  if (minWidth.isMD) {
    return (
      <ScrollViewWithHeaderTitle
        style={styles.innerContentContainerMD}
        contentContainerStyle={styles.innerContentContainerMD}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.innerContentContainerMD}>
          <Main style={containerStyles}>{children}</Main>
        </View>

        <AppFooter />
      </ScrollViewWithHeaderTitle>
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior="height"
      // NOTE: On iOS we rely on `automaticallyAdjustKeyboardInsets` property.
      enabled={Platform.OS === 'android'}
      keyboardVerticalOffset={190}
    >
      <ScrollViewWithHeaderTitle
        automaticallyAdjustKeyboardInsets
        contentContainerStyle={scrollContainerStyles}
      >
        {children}
      </ScrollViewWithHeaderTitle>
    </KeyboardAvoidingView>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  containerXS: {
    paddingTop: theme.spacing['12'],
  },
  containerSM: {
    paddingTop: theme.spacing['16'],
  },
  containerMD: {
    flexDirection: 'row',
  },
  scrollContainerXS: {
    paddingBottom: theme.spacing['6'],
  },
  scrollContainerSM: {
    paddingTop: theme.spacing['12'],
    paddingBottom: theme.spacing['10'],
  },
  innerContentContainerMD: {
    height: '100%',
  },
});
